import { Service, service, inject } from './di'
import { ConfigService, RuntimeConfig } from './ConfigService'
import { EVTrackingService } from './EVTrackingService'
import {
    IAnalyticsService,
    TRACKER_BASE,
    ShoppingListAction,
    CouponActions,
    CouponSource,
    LoyaltyCardSource,
    LoginSource,
    ShoppingListEventContext,
    FavouriteAction,
    FamilySyncAction,
    FavouriteContext,
    LandingPageActions,
    CrossBrowseAction,
    AggregateSourceAction,
    FlyerPlacement,
    gtag,
} from './definitions/IAnalyticsService'
import { Coupon } from '../definitions/CouponDefinitions'
import {
    SearchMode,
    LoyaltyProgram,
    Experiment,
    LoyaltyProgramCoupon,
    LoyaltyProgramMerchant,
    AuthProvider,
} from '../definitions/CommonDefinitions'
import { Flyer } from '../definitions/FlyerDefinitions'
import {
    BaseAnalyticsDelegate,
    AnalyticsParameters,
} from './analytics/BaseAnalyticsDelegate'

import { DistAnalyticsDelegate } from './analytics/DistAnalyticsDelegate'
import { FlippWebDistAnalyticsDelegate } from './analytics/FlippWebDistAnalyticsDelegate'
import { DefaultAnalyticsDelegate } from './analytics/DefaultAnalyticsDelegate'

@service('analyticsService')
export class AnalyticsService extends Service implements IAnalyticsService {
    @inject evTracker: EVTrackingService
    @inject config: ConfigService

    private delegate: IAnalyticsService

    init(val: RuntimeConfig) {
        if (this.delegate) {
            throw new Error(
                "Trying to call init() more than once on AnalyticsService. This shouldn't be done"
            )
        }

        switch (val) {
            case RuntimeConfig.DEFAULT:
                this.delegate = new DefaultAnalyticsDelegate()
                break
            case RuntimeConfig.DISTRIBUTION: {
                if (
                    this.config.isNewDistModuleAnalyticsEnabled() &&
                    !this.config.aidHero
                ) {
                    this.delegate = new FlippWebDistAnalyticsDelegate()
                } else {
                    this.delegate = new DistAnalyticsDelegate()
                }
                break
            }
        }

        if (val !== RuntimeConfig.PG)
            // Only track EVs internally for default or distribution experience
            this.evTracker.init(val)

        /* Initialize the base GA tracker */
        const profileId =
            val === RuntimeConfig.DISTRIBUTION
                ? this.config.DIST_GA_ACCOUNT
                : this.config.DEFAULT_GA_ACCOUNT
        ga('create', profileId, 'auto', TRACKER_BASE)
        ga('set', 'forceSSL', true)
        ga('set', 'dimension5', this.config.BUILD)

        /* Load the GTM script */
        this._loadGTagManagerScript(val)
    }

    isInitialized() {
        return this.delegate
    }

    get sessionId() {
        return this.delegate.sessionId
    }
    get channelId() {
        return this.delegate.channelId
    }
    get profileId() {
        return this.delegate.profileId
    }
    get analyticsParameters() {
        /* Filter our analytic parameters */
        const distParams = [
            'fsa',
            'flyer_id',
            'locale',
            'ga_profile',
            'background_mode',
            'traffic_source',
        ]
        const params = (
            this.delegate as BaseAnalyticsDelegate
        ).analyticsParameters()
        const result: AnalyticsParameters = {}
        Object.getOwnPropertyNames(params).forEach((name) => {
            if (distParams.indexOf(name) !== -1) {
                result[name] = params[name]
            }
        })
        result['background_mode'] = 'false' // Background mode should be false for future refreshes
        if (this.profileId) {
            result['ga_profile'] = this.profileId
        }
        return result
    }
    isFreshFlyer(flyerId: number) {
        return this.evTracker.isFreshFlyer(flyerId)
    }
    setPostalCode(postalCode: string) {
        if (this.delegate) {
            this.delegate.setPostalCode(postalCode)
        }
    }
    setLocale(locale: string) {
        if (this.delegate) {
            this.delegate.setLocale(locale)
        }
    }
    setAccountGuid(guid: string) {
        if (this.delegate) {
            this.delegate.setAccountGuid(guid)
        }
    }

    getAccountGuid(): string {
        if (this.delegate) {
            return this.delegate.getAccountGuid()
        }
    }

    isAnalyticsDelayed() {
        return this.delegate.isAnalyticsDelayed()
    }
    originFlyerId() {
        return this.delegate.originFlyerId()
    }
    siteLoaded() {
        this.delegate.siteLoaded()
    }

    trackPageView() {
        if (this.delegate) {
            return this.delegate.trackPageView()
        }
    }

    trackFlyerOpen(flyer: Flyer, sourceAction: AggregateSourceAction) {
        return this.delegate.trackFlyerOpen(flyer, sourceAction)
    }
    trackFlyerEngagement(flyer: Flyer, sourceAction: AggregateSourceAction) {
        return this.delegate.trackFlyerEngagement(flyer, sourceAction)
    }
    trackFlyerView(
        flyer: Flyer,
        left: number,
        top: number,
        width: number,
        height: number,
        zoomScale: number,
        interaction: number
    ) {
        return this.delegate.trackFlyerView(
            flyer,
            left,
            top,
            width,
            height,
            zoomScale,
            interaction
        )
    }
    trackFlyerItem(
        flyer: Flyer,
        itemId: number,
        st: number,
        alternateId?: string
    ) {
        this.delegate.trackFlyerItem(flyer, itemId, st, alternateId)
    }
    trackEcomItem(
        itemId: string,
        merchantId: number,
        merchant: string,
        st: number,
        isHidden: boolean
    ) {
        this.delegate.trackEcomItem(itemId, merchantId, merchant, st, isHidden)
    }
    trackSearch(
        q: string,
        flyerCount: number,
        tombstoneCount: number,
        itemCount: number,
        ecomItemCount: number,
        couponCount: number,
        couponMatchupCount: number,
        mode: SearchMode
    ) {
        this.delegate.trackSearch(
            q,
            flyerCount,
            tombstoneCount,
            itemCount,
            ecomItemCount,
            couponCount,
            couponMatchupCount,
            mode
        )
    }
    trackSearchFiltering(count: number, sortOrder: string) {
        this.delegate.trackSearchFiltering(count, sortOrder)
    }
    trackAdditionalResultsClick(count: number) {
        this.delegate.trackAdditionalResultsClick(count)
    }
    trackTombstone(q: string, merchant: string, merchantId: number) {
        this.delegate.trackTombstone(q, merchant, merchantId)
    }
    trackSearchItemClick(
        q: string,
        itemId: number,
        flyer: Flyer,
        rank: number
    ) {
        this.delegate.trackSearchItemClick(q, itemId, flyer, rank)
    }
    trackSearchFlyerClick(q: string, flyer: Flyer, rank: number) {
        this.delegate.trackSearchFlyerClick(q, flyer, rank)
    }
    trackSearchCouponClick(q: string, coupon: Coupon, rank: number) {
        this.delegate.trackSearchCouponClick(q, coupon, rank)
    }
    trackSearchEcomItemClick(
        q: string,
        itemId: string,
        merchantId: number,
        isHidden: boolean,
        rank: number
    ) {
        this.delegate.trackSearchEcomItemClick(
            q,
            itemId,
            merchantId,
            isHidden,
            rank
        )
    }
    trackCrossBrowse(
        type: CrossBrowseAction,
        from?: Flyer,
        to?: Flyer,
        idx?: number
    ) {
        this.delegate.trackCrossBrowse(type, from, to, idx)
    }
    trackMobileInstall(device: string, category: string = 'Mobile') {
        this.delegate.trackMobileInstall(device, category)
    }
    trackCouponPrint() {
        this.delegate.trackCouponPrint()
    }
    trackCoupon(
        coupon: Coupon,
        st: CouponActions,
        src: CouponSource,
        flyerId?: number,
        programMerchant?: LoyaltyProgramMerchant,
        loyaltyCoupon?: LoyaltyProgramCoupon
    ) {
        this.delegate.trackCoupon(
            coupon,
            st,
            src,
            flyerId,
            programMerchant,
            loyaltyCoupon
        )
    }
    trackVariant(experiment: Experiment, value: string) {
        this.delegate.trackVariant(experiment, value)
    }
    trackSignin(
        userGuid: string,
        provider: AuthProvider,
        context: LoginSource
    ) {
        this.delegate.trackSignin(userGuid, provider, context)
    }
    trackSignout(userGuid: string, provider: AuthProvider) {
        this.delegate.trackSignout(userGuid, provider)
    }
    trackWebGLDisabled() {
        this.delegate.trackWebGLDisabled()
    }
    trackLoyaltyCardAdded(program: LoyaltyProgram, context: LoyaltyCardSource) {
        this.delegate.trackLoyaltyCardAdded(program, context)
    }
    trackLoyaltyCardRemoved(programId: number) {
        this.delegate.trackLoyaltyCardRemoved(programId)
    }
    trackPageNotFoundEvent() {
        this.delegate.trackPageNotFoundEvent()
    }
    trackShoppingListChanged(
        item: string,
        listId: string,
        action: ShoppingListAction,
        context: ShoppingListEventContext,
        numClippings?: string
    ) {
        this.delegate.trackShoppingListChanged(
            item,
            listId,
            action,
            context,
            numClippings
        )
    }
    trackShoppingListPhotoDeleted(listId: string) {
        this.delegate.trackShoppingListPhotoDeleted(listId)
    }
    trackShoppingListRecommendedStream(
        action: string,
        stream: string,
        listId: string,
        item: string
    ) {
        this.delegate.trackShoppingListRecommendedStream(
            action,
            stream,
            listId,
            item
        )
    }
    trackSearchMiss(item: string, listId: string) {
        this.delegate.trackSearchMiss(item, listId)
    }
    trackAutocompleteHit(
        q: string,
        term: string,
        rank: number,
        context: string
    ) {
        this.delegate.trackAutocompleteHit(q, term, rank, context)
    }
    trackShoppingListPrint(listId: string, action: ShoppingListAction) {
        this.delegate.trackShoppingListPrint(listId, action)
    }
    trackShoppingListEvent(
        listId: string,
        action: ShoppingListAction,
        numAffected: number
    ) {
        this.delegate.trackShoppingListEvent(listId, action, numAffected)
    }
    trackFamilySyncEvent(
        listId: string,
        action: FamilySyncAction,
        inviteId?: string
    ) {
        this.delegate.trackFamilySyncEvent(listId, action, inviteId)
    }
    trackFavouriteEvent(
        merchantId: number,
        action: FavouriteAction,
        context: FavouriteContext
    ) {
        this.delegate.trackFavouriteEvent(merchantId, action, context)
    }
    trackOldBrowser() {
        this.delegate.trackOldBrowser()
    }

    trackFlyerListing(
        categoryName: string,
        flyers: Flyer[],
        sortOrder: string,
        favMerchants?: number[]
    ) {
        this.delegate.trackFlyerListing(
            categoryName,
            flyers,
            sortOrder,
            favMerchants
        )
    }

    trackPublisherBackButtonClick(publisherName: string) {
        this.delegate.trackPublisherBackButtonClick(publisherName)
    }

    trackPublisherBackButtonVisible(publisherName: string) {
        this.delegate.trackPublisherBackButtonVisible(publisherName)
    }

    trackFlyerClick(
        category: string,
        sort: string,
        placement: FlyerPlacement,
        position: number
    ) {
        this.delegate.trackFlyerClick(category, sort, placement, position)
    }

    trackCouponListing(
        categoryName: string,
        coupons: Coupon[],
        sortOrder: string
    ) {
        this.delegate.trackCouponListing(categoryName, coupons, sortOrder)
    }

    trackLandingPage(st: LandingPageActions, q?: string, flyer?: Flyer) {
        this.delegate.trackLandingPage(st, q, flyer)
    }

    trackUnsupportedBrowserErrorPage() {
        this.delegate.trackUnsupportedBrowserErrorPage()
    }

    trackFatalRuntimeErrorPage() {
        this.delegate.trackFatalRuntimeErrorPage()
    }

    trackLocationChanges(oldFsa: string, newFsa: string) {
        this.delegate.trackLocationChanges(oldFsa, newFsa)
    }

    trackShoppingListView(
        items: number,
        photos: number,
        flyerItems: number,
        ecomItems: number,
        coupon: number
    ) {
        this.delegate.trackShoppingListView(
            items,
            photos,
            flyerItems,
            ecomItems,
            coupon
        )
    }
    trackFlyerClose(flyer: Flyer, sourceAction: AggregateSourceAction) {
        return this.delegate.trackFlyerClose(flyer, sourceAction)
    }

    trackWindowClose() {
        if (this.delegate) {
            this.delegate.trackWindowClose()
        }
    }

    private _loadGTagManagerScript = (config: RuntimeConfig) => {
        // Don't load GTM tag if we're on P&G Everyday site
        if (config === RuntimeConfig.PG) return

        /* The snipped from the GTM developer site first pushes a 'gtm.start' event into
         * the dataLayer. Do that before loading the script*/
        dataLayer.push({
            originalLocation:
                document.location.protocol +
                '//' +
                document.location.hostname +
                document.location.pathname +
                document.location.search,
            'gtm.start': new Date().getTime(),
            event: 'gtm.js',
        })
        const firstScript = document.getElementsByTagName('script')[0]

        // https://flippit.atlassian.net/browse/SEO-877
        const acctId =
            config === RuntimeConfig.DISTRIBUTION
                ? this.config.DIST_GTAG_ACCOUNT
                : this.config.DEFAULT_GTAG_ACCOUNT
        const gtagScript = document.createElement('script')
        gtagScript.async = true
        gtagScript.src = `https://www.googletagmanager.com/gtag/js?id=${acctId}`
        firstScript.parentNode.insertBefore(gtagScript, firstScript)
        gtag('js', new Date())
        gtag('config', acctId)

        const gtmScript = document.createElement('script')
        gtmScript.async = true
        /* If the data layer is not called "dataLayer", we need to provide the new name as a
         * parameter 'l'. Eg:
         * 	https://www.googletagmanager.com/gtm.js?id=<gtm_account>&l=<dataLayer_name>
         * We are using the default name ('dataLayer') so don't pass it in
         */
        gtmScript.src = `//www.googletagmanager.com/gtm.js?id=${acctId}`
        firstScript.parentNode.insertBefore(gtmScript, firstScript)
    }
}
